import { useState } from "react";
import { Grid, Menu, IconButton, Divider } from "@mui/material";
import { Close, Dehaze } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CustomText from "./CustomText";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaPhone,
  FaWhatsapp,
} from "react-icons/fa";

const MenuRedirect = ({ locationes, isMobile, isDesktop }) => {
  /**
   * Function to navigate between routes.
   * @type {Function}
   */
  const navigate = useNavigate();

  /**
   * Estado que mantiene la referencia al elemento del ancla para el menú.
   * Inicialmente se establece como `null`, lo que indica que el menú no está abierto.
   * @const {Array} anchorEl - Estado que almacena el elemento al que se ancla el menú.
   * Es `null` cuando el menú está cerrado, o un elemento DOM cuando el menú está abierto.
   * @see useState
   */
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  /**
   * Función que alterna el estado de apertura del menú.
   * Si el menú está abierto, lo cierra estableciendo `anchorEl` como `null`.
   * Si el menú está cerrado, lo abre, estableciendo `anchorEl` con el elemento que dispara el evento.
   * @param {Object} event - El evento que dispara la acción de alternar el menú.
   * @param {EventTarget} event.currentTarget - El elemento que disparó el evento, que será el nuevo valor de `anchorEl` cuando el menú se abra.
   * @returns {void} No retorna ningún valor, solo actualiza el estado.
   */
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path) => {
    handleClose();
    navigate(path);
  };
  return (
    <>
      <IconButton
        edge="start"
        id="menu-button"
        aria-label="Abrir menú"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={open ? handleClose : handleOpen}
      >
        <Dehaze
          sx={{ color: !locationes ? "#847E7F" : "#FFF", ...design.Icon }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="long-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              maxHeight: isDesktop ? 150 * 3.8 : isMobile ? 150 * 4 : 150 * 4.5,
              width: isMobile ? "60%" : "15%",
              ...design.Menu,
            },
          },
          list: {
            "aria-labelledby": "menu-button",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Grid sx={design.alignRight}>
          <IconButton aria-label="Cerrar menú" onClick={handleClose}>
            <Close sx={design.Close} />
          </IconButton>
        </Grid>
        <ul style={design.ul}>
          {menuProm.map((doc) => {
            return (
              <li key={doc.name}>
                <CustomText
                  variant="body1"
                  text={doc.name}
                  onClick={() => handleNavigate(doc.path)}
                  fontFamily="Plus Jakarta Sans"
                  sx={design.Name}
                >
                  {doc.name}
                </CustomText>
              </li>
            );
          })}
        </ul>
        <Divider style={design.divider} />
        <CustomText variant="body1" sx={design.TextMedico}>
          Contacto
        </CustomText>
        <Grid container>
          {redesSociales.map((red, index) => (
            <IconButton
              key={index}
              component="a"
              href={red.url}
              target={red.url.startsWith("http") ? "_blank" : undefined}
              rel={
                red.url.startsWith("http") ? "noopener noreferrer" : undefined
              }
              sx={{ fontSize: 18 }}
            >
              {red.icon}
            </IconButton>
          ))}
        </Grid>
        <CustomText variant="body1" sx={design.TextMedico}>
          Legales
        </CustomText>
        <ul style={design.ul}>
          {legales.map((doc, index) => {
            return (
              <li key={index}>
                <CustomText
                  variant="body1"
                  text={doc.name}
                  onClick={() => handleNavigate(doc.path)}
                  fontFamily="Plus Jakarta Sans"
                  sx={design.Name}
                >
                  {doc.name}
                </CustomText>
              </li>
            );
          })}
        </ul>
        <Grid height={50} />
      </Menu>
    </>
  );
};

export default MenuRedirect;

const design = {
  Icon: {
    fontSize: 35,
  },
  Menu: {
    overflowY: "auto",
    elevation: 0,
    paddingLeft: 2,
    paddingRight: 3,
    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
    borderRadius: 2,
    mt: -1,
    pl: 3,
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "& .MuiAvatar-root": {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    "&::before": {
      content: '""',
      display: "block",
      position: "absolute",
      top: 10,
      right: 14,
      width: 10,
      height: 10,
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 0,
    },
  },
  alignRight: {
    display: "flex",
    justifyContent: "right",
    alignContent: "center",
    alignItems: "center",
  },
  Name: {
    display: "inline-block",
    fontSize: 15,
    paddingTop: 1,
    cursor: "pointer",
    "&:hover": {
      borderBottom: "2px solid #000",
    },
    textAlign: "left",
  },
  ul: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  Close: {
    color: "#000",
    fontSize: 30,
  },
  TextMedico: {
    fontFamily: "Plus Jakarta Sans",
    textAlign: "left",
    paddingTop: 1,
    fontSize: 23,
    fontWeight: "bold",
  },
  divider: {
    borderWidth: 1.8,
    marginTop: "10%",
  },
  transform: { horizontal: "right", vertical: "top" },
  anchor: { horizontal: "right", vertical: "bottom" },
};

const menuProm = [
  { name: "Inicio", path: "/" },
  { name: "Servicios", path: "/servicios" },
  { name: "Directorio de médicos", path: "/directorioMedico" },
  // { name: "Pre-ingreso", path: "/preRegistration" },
  { name: "¿Cómo llegar?", path: "/como-llegar" },
  { name: "Conoce el proyecto", path: "/conoceElProyecto" },
  { name: "Nosotros", path: "/mision-y-vision" },
  // { name: "Paquetes y/o promociones", path: "/packagesSpecial" },
  { name: "Preguntas frecuentes", path: "/preguntas-frecuentes" },
  // { name: "Boletín mensual", path: "/newsletter" },
  { name: "Aseguradoras", path: "/aseguradoras" },
];

// const medicos = [
//   { name: "Contacto", path: "/contacto-medicos" },
//   { name: "Únete a Medam", path: "/joinMEDAM" },
//   { name: "Preguntas frecuentes", path: "/preguntas-frecuentes" },
//   // { name: "Boletín mensual", path: "/newsletter" },
// ];

const legales = [
  { name: "Términos de uso", path: "/terminos-de-uso" },
  { name: "Políticas", path: "/politicas-del-sitio" },
  { name: "Preguntas frecuentes", path: "/preguntas-frecuentes" },
  { name: "Aviso de privacidad", path: "/aviso-de-privacidad" },
  { name: "Permiso COFEPRIS", path: "" },
];

const redesSociales = [
  {
    icon: <FaInstagram />,
    url: "https://www.instagram.com/centromedico_medam/",
  },
  {
    icon: <FaFacebookF />,
    url: "https://www.facebook.com/p/Centro-M%C3%A9dico-Medam-61560690296954/",
  },
  {
    icon: <FaWhatsapp />,
    url: "https://wa.me/525951234567",
  },
  {
    icon: <FaPhone />,
    url: "tel:+525959541234",
  },
  {
    icon: <FaEnvelope />,
    url: "mailto:contacto@centromedicomedam.com",
  },
];
